// Implements: F-UIX-020, F-UIX-015
/**
 * The dictionary entry of every closed value the editor offers, so a select
 * shows Spanish while it sends the backend's literal.
 *
 * Each map is keyed by its closed list's own type, so a value added to a list
 * without a label is a compile error, not a select that shows a raw literal.
 */

import type { TranslationKey } from "../../../shared/i18n";
import type { es } from "../../../shared/i18n/es";
import type { PaletteRole } from "../../../types/design";
import type {
  DENSITIES,
  FONT_STACKS,
  RADII,
  REGISTERS,
  TYPE_SCALES,
} from "../model/profileModel";

/** A key whose value holds no `{placeholder}`, so `t(key)` needs no parameters. */
type PlainKey<K extends TranslationKey> = K extends TranslationKey
  ? (typeof es)[K] extends `${string}{${string}}${string}`
    ? never
    : K
  : never;

/** A key of this module that takes no placeholder: what a label map may hold. */
export type DesignKey = PlainKey<Extract<TranslationKey, `design.${string}`>>;

/** A label for every member of a closed list, keyed by the member as the select sends it. */
export type Labels<List extends readonly (string | number)[]> = Readonly<
  Record<`${List[number]}`, DesignKey>
>;

export const FONT_LABELS: Labels<typeof FONT_STACKS> = {
  "system-sans": "design.font.systemSans",
  "system-serif": "design.font.systemSerif",
  "system-rounded": "design.font.systemRounded",
  "system-mono": "design.font.systemMono",
  inter: "design.font.inter",
  poppins: "design.font.poppins",
  manrope: "design.font.manrope",
  "dm-sans": "design.font.dmSans",
  montserrat: "design.font.montserrat",
  roboto: "design.font.roboto",
  lato: "design.font.lato",
  "source-serif-4": "design.font.sourceSerifFour",
  "playfair-display": "design.font.playfairDisplay",
  merriweather: "design.font.merriweather",
  "ibm-plex-mono": "design.font.ibmPlexMono",
};

export const SCALE_LABELS: Labels<typeof TYPE_SCALES> = {
  "1.2": "design.scale.minorThird",
  "1.25": "design.scale.majorThird",
  "1.333": "design.scale.perfectFourth",
};

export const REGISTER_LABELS: Labels<typeof REGISTERS> = {
  minimal: "design.register.minimal",
  dense: "design.register.dense",
  editorial: "design.register.editorial",
  corporate: "design.register.corporate",
};

export const DENSITY_LABELS: Labels<typeof DENSITIES> = {
  compact: "design.density.compact",
  regular: "design.density.regular",
  comfortable: "design.density.comfortable",
};

export const RADIUS_LABELS: Labels<typeof RADII> = {
  none: "design.radius.none",
  small: "design.radius.small",
  medium: "design.radius.medium",
  rounded: "design.radius.round",
  large: "design.radius.large",
};

export const ROLE_LABELS: Readonly<Record<PaletteRole, DesignKey>> = {
  primary: "design.role.primary",
  secondary: "design.role.secondary",
  surface: "design.role.surface",
  text: "design.role.text",
  success: "design.role.success",
  warning: "design.role.warning",
  danger: "design.role.danger",
};
